import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Sparkles, ArrowLeft, Download, Loader2, Globe, Users, Activity, Cloud, CheckCircle2, XCircle, Moon, Sun,
} from "lucide-react";

export default function AdminVisitors() {
  const { user, loading, setUser } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(true);
  const [whoami, setWhoami] = useState(null);

  const effectiveUser = user || location.state?.user;

  useEffect(() => {
    if (!user && location.state?.user) setUser?.(location.state.user);
  }, [user, location.state, setUser]);

  useEffect(() => {
    if (loading) return;
    if (!effectiveUser) { navigate("/", { replace: true }); return; }
    (async () => {
      try {
        const { data: w } = await api.get("/admin/whoami");
        setWhoami(w);
        if (!w.is_admin) { setError("You don't have access to this page."); setBusy(false); return; }
        const { data: v } = await api.get("/admin/visitors?limit=200");
        setData(v);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Failed to load.");
      } finally {
        setBusy(false);
      }
    })();
  }, [loading, effectiveUser, navigate]);

  const download = (fmt) => {
    // Force-authenticated download using the cookie
    window.open(`${API_BASE}/admin/visitors.${fmt}`, "_blank");
  };

  if (loading || busy) {
    return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-primary" /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <div className="max-w-md w-full rounded-3xl border border-border bg-card p-10 text-center">
          <XCircle className="mx-auto mb-4 text-destructive" size={40} />
          <h1 className="text-2xl mb-2">Access denied</h1>
          <p className="text-muted-foreground mb-3">{error}</p>
          {whoami && !whoami.admin_emails_configured && (
            <p className="text-xs text-muted-foreground mb-6">
              Ask the site owner to add your email to <code className="bg-muted rounded px-1">ADMIN_EMAILS</code> in the server env.
            </p>
          )}
          <Button onClick={() => navigate("/dashboard")} className="rounded-full h-11 px-6">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-grain">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-background/70 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="w-9 h-9 rounded-full border border-border grid place-items-center hover:bg-accent" title="Dashboard">
              <ArrowLeft size={15} />
            </Link>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground">
                <Sparkles size={16} />
              </div>
              <span className="font-display text-xl tracking-tight">Readlet</span>
              <span className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground ml-1">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggle} className="w-10 h-10 rounded-full border border-border grid place-items-center hover:bg-accent">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Button data-testid="download-xlsx-btn" onClick={() => download("xlsx")} className="rounded-full h-10 px-4">
              <Download size={15} className="mr-2" /> XLSX
            </Button>
            <Button data-testid="download-csv-btn" onClick={() => download("csv")} variant="secondary" className="rounded-full h-10 px-4">
              <Download size={15} className="mr-2" /> CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div>
          <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-3">Visitor analytics</p>
          <h1 className="text-4xl sm:text-5xl leading-tight mb-2">Who's reading Readlet.</h1>
          <p className="text-muted-foreground">Every visit is logged with IP, device, browser, country and timestamp.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total visits" value={data.total} testId="stat-total" />
          <StatCard icon={Activity} label="Today" value={data.today} testId="stat-today" />
          <StatCard icon={Globe} label="Unique visitors" value={data.unique} testId="stat-unique" />
          <StatCard
            icon={Cloud}
            label="Google Sheets sync"
            value={data.sheets_mirror_enabled ? "On" : "Off"}
            tone={data.sheets_mirror_enabled ? "primary" : "muted"}
            testId="stat-sheets"
          />
        </div>

        {/* Top countries */}
        {data.top_countries?.length > 0 && (
          <div className="rounded-3xl border border-border bg-card p-6">
            <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-4">Top countries</p>
            <div className="flex flex-wrap gap-2" data-testid="top-countries">
              {data.top_countries.map((c) => (
                <span key={c.country} className="rounded-full border border-border bg-background px-3 py-1.5 text-sm">
                  {c.country} · <span className="text-primary font-semibold">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="rounded-3xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold">Recent visits</p>
            <p className="text-xs text-muted-foreground">Showing {data.items?.length || 0} of {data.total}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="visitors-table">
              <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Time</th>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">IP</th>
                  <th className="text-left px-4 py-3">Device</th>
                  <th className="text-left px-4 py-3">Browser</th>
                  <th className="text-left px-4 py-3">OS</th>
                  <th className="text-left px-4 py-3">Path</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((v) => (
                  <tr key={v.id} className="border-t border-border hover:bg-accent/40">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{new Date(v.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-3">{v.country ? `${v.country_code ? v.country_code + " · " : ""}${v.country}` : "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{v.ip}</td>
                    <td className="px-4 py-3 capitalize">{v.device_type}</td>
                    <td className="px-4 py-3">{v.browser}</td>
                    <td className="px-4 py-3">{v.os}</td>
                    <td className="px-4 py-3 max-w-[220px] truncate" title={v.path}>{v.path || "/"}</td>
                  </tr>
                ))}
                {data.items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">No visits yet. Share your site!</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!data.sheets_mirror_enabled && (
          <div className="rounded-3xl border border-border bg-card p-6">
            <p className="text-xs tracking-[0.22em] uppercase text-primary font-semibold mb-2">Optional · Google Sheets mirror</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Set <code className="bg-muted rounded px-1">GOOGLE_SHEET_ID</code> and <code className="bg-muted rounded px-1">GOOGLE_SERVICE_ACCOUNT_JSON</code> in the backend env
              to have every visit appended to your Google Sheet in real time. Share the Sheet with the service account's email as an Editor.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "default", testId }) {
  return (
    <div className={`rounded-3xl border p-5 ${tone === "primary" ? "border-primary/40 bg-primary/[0.05]" : "border-border bg-card"}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2 text-muted-foreground">
        <Icon size={15} />
        <span className="text-xs uppercase tracking-[0.18em]">{label}</span>
      </div>
      <p className="font-display text-4xl leading-none">{value}</p>
    </div>
  );
}
